import React, { useEffect, useState } from "react";
import API, { API_BASE_URL } from "../services/api";
import { formatDateTimeDMY } from "../utils/dateFormat";

function Sales() {

  const [sales, setSales] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [eventSearch, setEventSearch] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [machines, setMachines] = useState([]);
  const [slots, setSlots] = useState([]);

  const [selectedMachine, setSelectedMachine] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");

  useEffect(() => {
    loadSales();
    loadTransactions();
    loadMachines();
  }, []);

  const loadSales = async () => {
    const res = await API.get("/sales");
    setSales(res.data);
  };

  const loadMachines = async () => {
    const res = await API.get("/machines");
    setMachines(res.data);
  };

  const loadTransactions = async () => {
    const res = await API.get("/sales/transactions?limit=50");
    setTransactions(res.data);
  };

  const loadSlots = async (machineId) => {
    const res = await API.get(`/stock/${machineId}`);
    setSlots(res.data);
  };

  const createSale = async () => {
    if (!selectedMachine || !selectedSlot) {
      alert("Debe seleccionar máquina y producto");
      return;
    }

    const slot = slots.find(s => s.id === parseInt(selectedSlot));

    try {
      await API.post("/sales", {
        machine_id: selectedMachine,
        slot_id: selectedSlot,
        amount: slot.price
      });

      
      loadSales();
      loadTransactions();
      loadSlots(selectedMachine);

    } catch (err) {
      alert(err.response?.data?.error || "Error al registrar venta");
    }
  };

  const exportExcel = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/sales/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ventas.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("No se pudo exportar el Excel");
    }
  };

  const formatCLP = value =>
    new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP"
    }).format(value || 0);

  const eventBadgeStyle = (eventType) => {
    const base = {
      fontSize: "12px",
      padding: "2px 8px",
      borderRadius: "999px",
      display: "inline-block",
      fontWeight: 600
    };

    if ((eventType || "").includes("approved")) {
      return { ...base, background: "#dcfce7", color: "#166534" };
    }
    if ((eventType || "").includes("rejected") || (eventType || "").includes("denied") || (eventType || "").includes("failed")) {
      return { ...base, background: "#fee2e2", color: "#991b1b" };
    }
    return { ...base, background: "#e2e8f0", color: "#334155" };
  };

  const filteredTransactions = transactions.filter((t) => {
    const matchesType =
      eventTypeFilter === "all" ? true : (t.event_type || "").includes(eventTypeFilter);

    const q = eventSearch.trim().toLowerCase();
    if (!q) return matchesType;

    const blob = [
      t.external_id,
      t.event_type,
      t.machine_name,
      t.slot_code,
      t.device_name,
      t.reason
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return matchesType && blob.includes(q);
  });

  return (
    <div>
      <header className="page-header">
        <h1 className="page-title">Ventas</h1>
        <p className="page-subtitle">
          Registro manual, exportación y seguimiento de eventos de transacción.
        </p>
      </header>

      <div className="card card--padded" style={{ marginBottom: "1.5rem" }}>
        <h2 className="section-title">Registrar venta</h2>
        <div className="field-row">
          <select
            className="select"
            value={selectedMachine}
            onChange={e => {
              setSelectedMachine(e.target.value);
              loadSlots(e.target.value);
            }}
          >
            <option value="">Seleccionar máquina</option>
            {machines.map(m => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <select
            className="select"
            value={selectedSlot}
            onChange={e => setSelectedSlot(e.target.value)}
            style={{ minWidth: "240px" }}
          >
            <option value="">Seleccionar producto</option>
            {slots.map(s => (
              <option key={s.id} value={s.id}>
                {s.slot_code} — {s.product_name} ({formatCLP(s.price)}) · stock {s.stock}
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-primary" onClick={createSale}>
            Vender
          </button>
        </div>
      </div>

      <div className="toolbar">
        <button type="button" className="btn btn-secondary" onClick={exportExcel}>
          Exportar Excel
        </button>
      </div>

      <div className="table-wrap" style={{ marginTop: "1rem" }}>
        <table className="table-modern">
          <thead>
            <tr>
              <th>Máquina</th>
              <th>Slot</th>
              <th>Producto</th>
              <th>Monto</th>
              <th>Fecha</th>
            </tr>
          </thead>
          <tbody>
            {sales.map(s => (
              <tr key={s.id}>
                <td>{s.machine_name}</td>
                <td>{s.slot_code}</td>
                <td>{s.product_name}</td>
                <td>{formatCLP(s.amount)}</td>
                <td>{formatDateTimeDMY(s.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="section-title" style={{ marginTop: "2rem" }}>
        Eventos de transacción
      </h2>
      <div className="card card--padded" style={{ marginBottom: "0.75rem" }}>
        <div className="toolbar" style={{ marginBottom: 0 }}>
          <input
            className="input"
            placeholder="Buscar por external_id, evento, máquina, detalle..."
            value={eventSearch}
            onChange={e => setEventSearch(e.target.value)}
            style={{ minWidth: "min(100%, 320px)", flex: "1 1 200px" }}
          />
          <select
            className="select"
            value={eventTypeFilter}
            onChange={e => setEventTypeFilter(e.target.value)}
          >
            <option value="all">Todos los eventos</option>
            <option value="started">Inicio pago</option>
            <option value="approved">Aprobados</option>
            <option value="rejected">Rechazados</option>
            <option value="denied">Denegados</option>
            <option value="failed">Fallidos</option>
          </select>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setEventSearch("");
              setEventTypeFilter("all");
            }}
          >
            Limpiar
          </button>
          <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
            {filteredTransactions.length} de {transactions.length}
          </span>
        </div>
      </div>

      <div className="table-wrap" style={{ maxHeight: "420px" }}>
        <table className="table-modern">
          <thead>
            <tr>
              <th style={{ position: "sticky", top: 0, zIndex: 1 }}>Fecha</th>
              <th style={{ position: "sticky", top: 0, zIndex: 1 }}>External ID</th>
              <th style={{ position: "sticky", top: 0, zIndex: 1 }}>Evento</th>
              <th style={{ position: "sticky", top: 0, zIndex: 1 }}>Máquina</th>
              <th style={{ position: "sticky", top: 0, zIndex: 1 }}>Slot</th>
              <th style={{ position: "sticky", top: 0, zIndex: 1 }}>Dispositivo</th>
              <th style={{ position: "sticky", top: 0, zIndex: 1 }}>Detalle</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map(t => (
              <tr key={t.id}>
                <td>{formatDateTimeDMY(t.created_at)}</td>
                <td style={{ maxWidth: "260px", wordBreak: "break-all" }}>{t.external_id || "—"}</td>
                <td>
                  <span style={eventBadgeStyle(t.event_type)}>{t.event_type}</span>
                </td>
                <td>{t.machine_name || t.machine_id || "—"}</td>
                <td>{t.slot_code || t.slot_id || "—"}</td>
                <td>{t.device_name || t.device_id || "—"}</td>
                <td>{t.reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Sales;